import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { LockOutlined, Warning } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import AiInsightsLogo from '@/components/layout/AiInsightsLogo';

/**
 * AuthLogin component that displays a warning if the user is not authenticated
 * and handles the authentication with a JWT token.
 *
 * @returns {React.FC}
 */
const AuthLogin: React.FC = () => {
    const [authError, setAuthError] = useState<string | null>(null);
    const isMountedRef = useRef<boolean>(true);

    const { authenticate, isLoading, error, isAuthenticated } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const isSessionExpired = searchParams.get('error') === 'session_expired';

    const getAuthMessage = () => {
        if (authError) return authError;
        if (error) return `Authentication failed: ${error}`;

        return 'Authentication required';
    };

    useEffect(() => {
        const handleAuthentication = async () => {
            const jwt = searchParams.get('jwt');

            if (!jwt) {
                const errorMessage = isSessionExpired
                    ? 'Your session has expired. Please reload the page to log in again.'
                    : 'Unable to authenticate: No JWT token provided';

                setAuthError(errorMessage);

                return;
            }

            try {
                const isAuthorized = await authenticate(jwt);

                if (isAuthorized && isMountedRef.current) {
                    navigate('/', { replace: true });
                }
            } catch (err) {
                if (isMountedRef.current) {
                    setAuthError(err instanceof Error ? err.message : 'An error occurred during authentication');
                }
            }
        };

        handleAuthentication();
    }, [searchParams, authenticate, navigate, isSessionExpired]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const hasError = Boolean(authError || error);
    const alertIcon = hasError ? <Warning /> : <LockOutlined />;
    const authMessage = getAuthMessage();

    return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" p={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mb: 1.5 }}>
                <Box sx={{ width: 24, height: 24, ml: 0.15 }}>
                    <AiInsightsLogo variant="primary" />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', ml: 0.75, color: 'primary.main' }}>
                    AI Insights
                </Typography>
            </Box>
            {isLoading && <CircularProgress />}
            {isAuthenticated && (
                <Typography variant="body2" gutterBottom>
                    Authentication successful. <Link to="/">Continue</Link> to home page.
                </Typography>
            )}
            {!isLoading && !isAuthenticated && (
                <Alert severity="warning" icon={alertIcon} elevation={1} sx={{ width: '100%', maxWidth: 400 }}>
                    <Typography variant="body2">{authMessage}</Typography>
                </Alert>
            )}
        </Box>
    );
};

export default AuthLogin;
